import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VOICE_AUTO_STOP_EVENT } from '@/lib/voice-auto-stop-events';
import styles from './AutoStopToast.module.css';

/**
 * V.A.3: bottom-anchored toast surfaced after a recording was silently
 * auto-stopped at the 10-minute cap and transcription completed
 * successfully. Auto-dismiss after 5s + tap-to-dismiss. One-time per
 * auto-stop event (the dispatcher fires once per success branch hit;
 * subsequent recordings get their own dispatch).
 *
 * Uses aria-live="polite" (informational, not error-class) so screen
 * readers announce the cap context after the transcribed text lands
 * without interrupting whatever the reader was on.
 *
 * Triggered by a window event `adia:voice-auto-stop` dispatched from
 * useSpeechToText's enhanced-mode onstop success branch when the
 * recorder's __autoStopped flag is set. The event constant +
 * dispatcher live in `@/lib/voice-auto-stop-events` so this `.tsx`
 * file exports only the component (fast refresh requirement).
 *
 * Mounted globally inside VoiceCaptureProvider alongside
 * VoiceErrorToast so all five voice-capture surfaces (PrayPage,
 * PrayerDetail update form, UpdateSheet update + testimony variants,
 * PrayerDetail TestimonyModal) get coverage from a single mount.
 */

export default function AutoStopToast() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onAutoStop = () => {
      setOpen(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setOpen(false);
        timer = null;
      }, 5000);
    };
    window.addEventListener(VOICE_AUTO_STOP_EVENT, onAutoStop);
    return () => {
      window.removeEventListener(VOICE_AUTO_STOP_EVENT, onAutoStop);
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (!open) return null;

  return (
    <button
      type="button"
      className={styles.toast}
      onClick={() => setOpen(false)}
      role="status"
      aria-live="polite"
      aria-label={t('voice.autoStopToast')}
    >
      <span className={styles.icon} aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      </span>
      <span className={styles.message}>{t('voice.autoStopToast')}</span>
    </button>
  );
}
